'use client';

import { useEffect, useRef, useState } from 'react';
import { Loader2, ArrowDown } from 'lucide-react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { useNativeApp } from '@/lib/native';

// Pull distances in CSS px, after the resistance factor.
const TRIGGER = 70; // pull past this (then release) to refresh
const MAX = 120; // visual cap on the stretch
const RESIST = 0.5; // finger moves 2px per 1px of stretch

/**
 * Pull-to-refresh for the native shell (iOS + Android).
 *
 * The native layer disables the WebView's overscroll/bounce (so the page's
 * fixed header & footer never move), and this component owns the whole gesture:
 * while you drag down from the top it translates the page's <main> content down
 * (an elastic stretch) and reveals a spinner below the header; releasing past
 * the threshold reloads the page from the server for fresh data.
 *
 * Only <main> is transformed — it's a sibling of the fixed header/footer, never
 * their ancestor, so they stay perfectly still. On web this renders nothing and
 * adds no listeners.
 */
export default function PullToRefresh() {
  const { isNative } = useNativeApp();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const pullRef = useRef(0);
  const startY = useRef(0);
  const active = useRef(false);
  const mainEl = useRef<HTMLElement | null>(null);
  const refreshingRef = useRef(false);

  useEffect(() => {
    if (!isNative) return;

    const scrollTop = () =>
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const moveMain = (v: number, animate: boolean) => {
      const el = mainEl.current;
      if (!el) return;
      el.style.transition = animate ? 'transform 0.25s ease-out' : 'none';
      el.style.transform = v > 0 ? `translateY(${v}px)` : '';
    };

    const setDist = (v: number) => {
      pullRef.current = v;
      setPull(v);
      moveMain(v, !active.current);
    };

    const onStart = (e: TouchEvent) => {
      if (refreshingRef.current || e.touches.length !== 1) {
        active.current = false;
        return;
      }
      if (scrollTop() <= 0) {
        startY.current = e.touches[0].clientY;
        mainEl.current = document.querySelector('main');
        active.current = true;
      } else {
        active.current = false;
      }
    };

    const onMove = (e: TouchEvent) => {
      if (!active.current || refreshingRef.current) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && scrollTop() <= 0) {
        e.preventDefault();
        setDist(Math.min(MAX, dy * RESIST));
      } else if (dy <= 0) {
        active.current = false;
        if (pullRef.current !== 0) setDist(0);
      }
    };

    const onEnd = () => {
      if (!active.current || refreshingRef.current) {
        active.current = false;
        return;
      }
      active.current = false;
      if (pullRef.current >= TRIGGER) {
        refreshingRef.current = true;
        setRefreshing(true);
        pullRef.current = TRIGGER;
        setPull(TRIGGER);
        moveMain(TRIGGER, true);
        Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
        // Let the spinner paint a frame, then reload from the server. The full
        // reload resets the <main> transform on its own.
        window.setTimeout(() => window.location.reload(), 150);
      } else {
        setDist(0);
      }
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
      moveMain(0, false);
    };
  }, [isNative]);

  if (!isNative) return null;

  const visible = pull > 0 || refreshing;
  const progress = Math.min(1, pull / TRIGGER);

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        // Pin just below the fixed app header; the circle emerges into the gap
        // that opens up as <main> stretches down.
        top: 'calc(env(safe-area-inset-top, 0px) + var(--header-height, 56px))',
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 40,
        transform: `translateY(${visible ? Math.max(0, pull - 40) : -8}px)`,
        transition: active.current ? 'none' : 'transform 0.25s ease-out, opacity 0.2s',
        opacity: visible ? 1 : 0,
      }}
    >
      <div
        style={{
          height: 34,
          width: 34,
          borderRadius: '9999px',
          background: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {refreshing ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        ) : (
          <ArrowDown
            className="h-5 w-5 text-primary"
            style={{
              transform: `rotate(${progress * 180}deg)`,
              transition: 'transform 0.1s',
            }}
          />
        )}
      </div>
    </div>
  );
}
